import React, { useState } from "react";
import { useNotify, useRefresh, Button } from "react-admin";
import {
  Button as MuiButton, Dialog, DialogActions, DialogContent,
  DialogTitle, TextField,
} from "@material-ui/core";
import BlockIcon from "@material-ui/icons/Block";
import RestoreIcon from "@material-ui/icons/SettingsBackupRestore";
import { logError, safeAPI } from "../../../utils/appUtils";

/** Global suspension (spec §5): status="SUSPENDED" blocks this driver at EVERY
 * yard — driverApi re-checks it on every request, so it bites mid-session.
 * This power belongs to the carrier alone: they own the employment
 * relationship, so only they may suspend or lift globally. Yard managers block
 * locally through the junction's blockAccess instead — otherwise any yard
 * could lift a suspension another yard (or the carrier) imposed.
 *
 * The write goes through driverAdmin rather than updateCarrierUser: CarrierUser
 * rows span yards, so the model carries no owner or groups field to scope on
 * and its AppSync rule is `allow: private` — every signed-in user. The ACL check
 * that renders this button is therefore advisory only, and the endpoint re-walks
 * the junction graph to confirm the caller really is active dispatch of a
 * carrier this driver belongs to. */
export const SuspendDriverButton = ({ record }) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  // Drivers only: dispatch colleagues are managed through yard access and
  // delete, and they need their portal login decided elsewhere.
  if (!record || record.userType !== "driver") return null;
  const suspended = record.status === "SUSPENDED";

  const apply = async () => {
    try {
      // suspendedBy is stamped server-side from the IAM-authenticated Cognito
      // principal — a client-supplied name would be unverifiable on an audit
      // field whose whole point is attribution.
      await safeAPI.post("transREST", "/driverAdmin/suspendDriver", {
        body: {
          carrierUserId: record.id,
          suspend: !suspended,
          reason: suspended ? undefined : reason || undefined,
        },
      });
      notify(suspended ? "Suspension lifted." : "Driver suspended everywhere.");
      setOpen(false);
      refresh();
    } catch (err) {
      logError("carrierApp:carrierUsers:SuspendDriverButton", err, notify);
    }
  };

  return (
    <>
      <Button
        label={suspended ? "Lift suspension" : "Suspend everywhere"}
        onClick={() => (suspended ? apply() : setOpen(true))}
      >
        {suspended ? <RestoreIcon /> : <BlockIcon />}
      </Button>
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth>
        <DialogTitle>Suspend {record.firstName} {record.lastName} at ALL yards?</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <MuiButton onClick={() => setOpen(false)}>Cancel</MuiButton>
          <MuiButton
            color="secondary"
            onClick={apply}
            disabled={!reason.trim()}
          >
            Suspend
          </MuiButton>
        </DialogActions>
      </Dialog>
    </>
  );
};
